import { useMemo } from 'react';

import { UserContextType } from './User.context';
import { Role } from './User.interface';

export interface PermissionProps {
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
}

export interface Permission extends PermissionProps {
  not: PermissionProps;
}

function createPermission(props: PermissionProps): Permission {
  return {
    ...props,
    not: {
      read: !props.read,
      create: !props.create,
      update: !props.update,
      delete: !props.delete
    }
  };
}

export function getUserPermissions(roles: Role[]) {
  // if has roles
  const any = () => roles.length > 0;
  // if some roles match
  const has = (...names: string[]) => {
    return (
      roles.length > 0 &&
      names.length > 0 &&
      roles.some(role => role.name && names.includes(role.name))
    );
  };
  // if roles have exact match
  const only = (...names: string[]) => {
    return (
      roles.length > 0 &&
      names.length > 0 &&
      roles.length === names.length &&
      names.every(name => roles.some(role => role.name === name))
    );
  };

  // NOTE: based on /portal/documents guards
  // make sure user cannot edit and has a DocumentViewer role
  const isDocAdmin = has('Admin', 'SiteAdmin', 'DocumentAdmin');
  const isDocEditor = has('DocumentEditor');
  const isDocViewer = has('DocumentViewer') && !(isDocAdmin || isDocEditor);
  const isDocHistoryViewer = has('DocumentHistoryViewer');
  const file: Permission = createPermission({
    read: any(),
    create: !isDocViewer,
    delete: isDocAdmin,
    update: !isDocViewer
  });
  return { any, has, only, file, isDocViewer, isDocHistoryViewer };
}

export function useUserPermissions(user: UserContextType) {
  return useMemo(() => getUserPermissions(user?.roles || []), [user]);
}
